import { ConsentCard } from '@almbrand/consentcard';
import { Modal } from '@almbrand/modal';
import { RichText } from '@almbrand/richtext';
import { stripHtml } from 'common/StringUtil';
import { HttpStatusCode } from 'constantsValues';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { fetchConsents, updateConsents } from 'store/slices/consentSlice';
import { getStatusMessage } from 'utilities/statusMessageUtils';
import styles from './ConsentCardComponent.module.scss';

export interface ConsentCardComponentProps {
	title?: string;
	showExpandButton?: boolean;
	expandButtonText?: string;
	collapseButtonText?: string;
	useReadline?: boolean;
	contentType?: string;
	contentId?: number;
	contentName?: string;
	statusMessages?: {
		items: Array<{
			content: {
				statusMessage: string;
				statusCode: HttpStatusCode;
			};
		}>;
	};
}

// Renders consent rows from the API and maps clicked rows back to consent update requests.
export const ConsentCardComponent: React.FC<ConsentCardComponentProps> = ({
	title = 'Samtykker',
	showExpandButton = true,
	expandButtonText = 'Vis alt',
	collapseButtonText = 'Vis mindre',
	statusMessages,
}) => {
	const dispatch = useAppDispatch();
	const consents = useAppSelector((state) => state.consents.consents) ?? [];
	const consentStatus = useAppSelector((state) => state.consents.status);
	const updateStatus = useAppSelector((state) => state.consents.updateStatus);
	const [errorDismissed, setErrorDismissed] = useState(false);

	useEffect(() => {
		if (consentStatus === HttpStatusCode.Idle) {
			dispatch(fetchConsents());
		}
	}, [consentStatus, dispatch]);

	useEffect(() => {
		if (updateStatus === HttpStatusCode.Loading) {
			setErrorDismissed(false);
		}
	}, [updateStatus]);

	const consentsList = useMemo(() => {
		return consents.flatMap((consent) => {
			const parentChecked = consent?.consent_channels?.some((channel) => channel?.channel_is_active) ?? false;
			return [
				{
					id: consent?.consent_developer_name ?? '',
					title: consent?.consent_name ?? '',
					description: stripHtml(consent?.consent_text ?? ''),
					checked: parentChecked,
					type: 'parent',
				},
				...(consent?.consent_channels?.map((channel) => ({
					id: channel?.channel_developer_name ?? '',
					title: channel?.channel_business_name ?? '',
					checked: channel?.channel_is_active ?? false,
					type: 'child',
					parentId: consent?.consent_developer_name ?? '',
				})) ?? []),
			];
		});
	}, [consents]);

	// Finds the clicked row, rebuilds the correct payload, and sends it back to the API.
	const handleToggle = useCallback(
		(item: any) => {
			const clickedConsent = consentsList.find((consent) => consent.id === item.id);
			if (!clickedConsent) return;

			const nextChecked = !clickedConsent.checked;

			const payload = consentsList
				.filter((consent) => consent.type === 'child')
				.map((consent) => {
					if (consent.id === clickedConsent.id) {
						return { type: consent.id, value: String(nextChecked) };
					}
					// Propagate parent toggle to all its children
					if (
						clickedConsent.type === 'parent' &&
						'parentId' in consent &&
						consent.parentId === clickedConsent.id
					) {
						return { type: consent.id, value: String(nextChecked) };
					}
					return { type: consent.id, value: String(consent.checked) };
				});

			dispatch(updateConsents(payload));
		},
		[consentsList, dispatch]
	);

	const shouldShowStatusMessage =
		consentStatus === HttpStatusCode.InternalServerError || consentStatus === HttpStatusCode.NoContent;
	const showUpdateError = updateStatus === HttpStatusCode.InternalServerError && !errorDismissed;

	return (
		<>
			{shouldShowStatusMessage && (
				<RichText
					className={`${styles.statusMessage} RichText`}
					description={getStatusMessage(statusMessages, consentStatus)}
				/>
			)}

			{!shouldShowStatusMessage && (
				<ConsentCard
					title={title}
					consents={consentsList}
					onConsentToggle={handleToggle}
					showExpandButton={showExpandButton}
					expandButtonText={expandButtonText}
					collapseButtonText={collapseButtonText}
				/>
			)}

			{showUpdateError && (
				<Modal
					mode='default'
					mobileVariation='default'
					title='Samtykket kunne ikke opdateres'
					ariaClose='Luk'
					closeButtonProps={{ type: 'primary', title: 'Luk' }}
					handleClose={() => setErrorDismissed(true)}
				>
					<div>Vi kunne ikke gemme din ændring. Prøv igen om lidt.</div>
				</Modal>
			)}
		</>
	);
};
